import { useEffect, useMemo, useState } from "react";
import { Luggage, Search, ExternalLink, BadgeCheck } from "lucide-react";
import api from "../lib/api";

/* Public baggage fees & rules reference. Charges are labeled
   'Typical / Representative' with a 'Check airline rules' link on every row
   (per owner spec — fees vary sharply by fare and route). */

export default function BaggageFees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/baggage").then((r) => setItems(r.data.items)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((i) => i.airline_name.toLowerCase().includes(t) || i.airline_code.toLowerCase().includes(t));
  }, [items, q]);

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12" data-testid="baggage-page">
      <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight flex items-center gap-3">
        <Luggage className="w-10 h-10 text-violet-700" /> Baggage Fees &amp; Rules
      </h1>
      <p className="text-muted-foreground mt-3 mb-2 max-w-3xl">
        Carry-on limits and <strong>typical / representative</strong> checked-bag charges by airline.
        Actual fees vary dramatically by fare class and route — always confirm with the airline link on each row.
      </p>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 inline-block px-3 py-1.5 mb-8" data-testid="baggage-disclaimer">
        Example: one airline's first international bag can be $85 on certain Basic Economy transatlantic routes yet free to some Asian markets.
      </p>

      <div className="relative mb-8">
        <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search airline — e.g. Lufthansa, EK, Delta…"
          data-testid="baggage-search-input"
          className="w-full border-2 border-zinc-300 pl-12 pr-4 py-4 text-base outline-none focus:border-violet-600 transition-colors" />
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : filtered.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="baggage-no-results">No airline matches — we're adding more all the time.</p>
        : (
        <div className="overflow-x-auto border border-zinc-300 bg-white">
          <table className="w-full text-sm" data-testid="baggage-table">
            <thead>
              <tr className="bg-violet-700 text-white text-left">
                <th className="px-3 py-3 font-700">Airline</th>
                <th className="px-3 py-3 font-700">Carry-on size</th>
                <th className="px-3 py-3 font-700">Carry-on weight</th>
                <th className="px-3 py-3 font-700">Typical checked allowance</th>
                <th className="px-3 py-3 font-700">Typical / Representative 1st bag charge*</th>
                <th className="px-3 py-3 font-700">Typical 2nd bag charge*</th>
                <th className="px-3 py-3 font-700">Verified</th>
                <th className="px-3 py-3 font-700"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const r = a.checked?.[0] || {};
                const bag = (b) => b?.included ? "Usually included" : [b?.online_price, b?.airport_price && `${b.airport_price} airport`].filter(Boolean).join(" / ") || "—";
                return (
                  <tr key={a.airline_code} className="border-t border-zinc-200 hover:bg-violet-50/50 align-top" data-testid={`baggage-row-${a.airline_code}`}>
                    <td className="px-3 py-3 font-700 whitespace-nowrap">{a.airline_name} <span className="text-xs text-muted-foreground font-500">({a.airline_code})</span>
                      {a.notes && <div className="text-xs text-amber-700 font-500 max-w-[220px] mt-1 whitespace-normal">{a.notes}</div>}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">{a.carry_on?.dimensions || "—"}</td>
                    <td className="px-3 py-3">{a.carry_on?.weight || "—"}</td>
                    <td className="px-3 py-3 max-w-[200px]">{(r.notes || "").replace("Typical checked allowance: ", "") || "—"}</td>
                    <td className="px-3 py-3">{bag(r.bag1)}</td>
                    <td className="px-3 py-3">{bag(r.bag2)}</td>
                    <td className="px-3 py-3 whitespace-nowrap"><span className="inline-flex items-center gap-1 text-xs text-emerald-700"><BadgeCheck className="w-3.5 h-3.5" />{a.last_verified || "—"}</span></td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      <a href={a.official_url} target="_blank" rel="noreferrer" data-testid={`baggage-official-${a.airline_code}`}
                        className="inline-flex items-center gap-1 text-xs font-700 text-violet-700 hover:underline">
                        Check airline rules <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-6 max-w-3xl">
        * These are representative published figures, not universal prices. Checked-bag charges change
        by fare class, route region, loyalty status and date. Verify current rules with your airline
        before packing — especially for Basic Economy and international itineraries.
      </p>
    </div>
  );
}
